import React, { FC, useContext, useEffect, useReducer, useState, useMemo, useRef } from "react";
import moment from "moment";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import ExceptionsService from "@app/exceptions/exceptionsService";
import {
  BulkExceptionEditModel,
  EditExceptionModel,
  SupplementaryDocument,
  UpdateSpecialtyGroupException, SpecialtyGroupException
} from "@app/exceptions/interfaces";
import { toast } from "react-toastify";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDropMultiFileUploader";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import {
  DragAndDropMultiFileUploaderContext,
  DragAndDropMultiFileUploaderContextProvider
} from "@app/shared/DragAndDropMultiFileUploaderContext";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import BulkEditConfirmationModal from "@app/exceptions/BulkEditConfirmationModal";
import {
  EditExceptionForm,
  EditExceptionFormActionType,
  editExceptionFormReducer,
  hasChanges,
  hasValidFields,
  normalizeSpaces
} from "@app/exceptions/editExceptionReducer";
import {
  SpecialtyGroupExceptionGrade,
  SpecialtyGroupExceptionReviewStatus,
  SpecialtyGroupExceptionReviewStatusLabels,
  SpecialtyGroupExceptionStatus
} from "@app/exceptions/enums";
import useShowSharedInsightsForClient from "@app/projects/network-assessment/adequacy/hooks/useShowSharedInsightsForClient";
import {
  getExceptionSharedInsightsMessage,
  getGapSharedInsightsMessage
} from "@app/exceptions/sharedInsightsMessages";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { useQuery } from "@tanstack/react-query";

type EditExceptionFlyoutProps = {
  editException?: EditExceptionModel;
  exceptionStatusOptions: DropdownItem<string>[];
  isForArchived?: boolean;
  isForBulkEdit?: boolean;
  refreshData?: () => void;
  isBulkCCIIO?: boolean | undefined;
}

const editExceptionFlyoutFormInitialState: EditExceptionForm = {
  comment: '',
  endDate: '',
  excludeFromCCIIOJustification: false,
  grade: SpecialtyGroupExceptionGrade.None,
  narrative: '',
  originalComment: '',
  originalEndDate: '',
  originalExcludeFromCCIIOJustification: false,
  originalGrade: SpecialtyGroupExceptionGrade.None,
  originalNarrative: '',
  originalPreviouslyApproved: false,
  originalReviewStatus: SpecialtyGroupExceptionReviewStatus.None,
  originalStartDate: '',
  originalStatus: SpecialtyGroupExceptionStatus.None,
  originalUploadedDocuments: [],
  previouslyApproved: false,
  reviewStatus: SpecialtyGroupExceptionReviewStatus.None,
  startDate: '',
  status: SpecialtyGroupExceptionStatus.None,
  uploadedDocuments: [],
  validEndDate: true,
  validStartDate: true,
}

const WrappedEditExceptionFlyout: FC<React.PropsWithChildren<EditExceptionFlyoutProps>> = (props: EditExceptionFlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext)
  const userContext = useContext(UserContext);
  const showSharedInsightsForClient = useShowSharedInsightsForClient();
  const exceptionsService = useRef(new ExceptionsService()).current;
  const uploadDocumentsApiKey: string = "document";
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const [isBulkEditConfirmationModalOpen, setIsBulkEditConfirmationModalOpen] = useState(false);
  const [mixedFields, setMixedFields] = useState<Set<string>>(new Set());

  const { data: bulkExceptions, isLoading: isLoadingBulkDetails } = useQuery({
    enabled: !!props.isForBulkEdit && exceptionFilterContext.selectedExceptionIds.length > 0,
    queryFn: ({ signal }) => exceptionsService.getSpecialtyGroupExceptionsByIds(exceptionFilterContext.selectedExceptionIds, signal),
    queryKey: [ExceptionsService.getSpecialtyGroupExceptionsByIdsQueryKey, exceptionFilterContext.selectedExceptionIds],
    staleTime: 5 * 60 * 1000,
  });

  const { data: isCCIIO } = useQuery({
    enabled: !!props.editException?.customProjectId && props.isBulkCCIIO === undefined && featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT),
    queryFn: ({ signal }) => exceptionsService.getCCIIOExceptionStatus([props.editException.customProjectId], signal),
    queryKey: [ExceptionsService.getCCIIOExceptionStatusQueryKey, props.editException?.customProjectId],
    staleTime: 5 * 60 * 1000,
  })

  const gradeContainerClasses = "mt-3 p-0";
  const disableGradeEdits = !userContext.roles.includes(Roles.SPECIALTY_EXCEPTION_REVIEWER_ADMIN);
  const disableReviewStatusEdits = !userContext.roles.includes(Roles.EXCEPTION_MANAGER);

  const [formState, formStateDispatch] = useReducer(editExceptionFormReducer, editExceptionFlyoutFormInitialState);

  const isSaveEnabled = () => {
    if (props.isForBulkEdit) {
      return !isLoadingBulkDetails && hasChanges(formState) && formState.validStartDate && formState.validEndDate;
    }
    return hasChanges(formState) && hasValidFields(formState) && (!props.isForArchived || formState.originalStatus !== formState.status);
  }

  const getExceptionDetails = async () => {
    const { customProjectId, exceptionId } = props.editException;
    const details: SpecialtyGroupException = await exceptionsService.getSpecialtyGroupException(customProjectId, exceptionId);
    details.supplementaryDocuments.forEach(item => {
      multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(item.supplementaryDocumentMetadataId, new File([], item.documentName)));
    });
    const originalStartDate = props.editException?.startDate !== '---' && !props.isForBulkEdit ?
      moment(props.editException?.startDate).format("YYYY-MM-DD") :
      '';
    const originalEndDate = props.editException?.endDate !== '---' && !props.isForBulkEdit ?
      moment(props.editException?.endDate).format("YYYY-MM-DD") :
      '';
    const originalPreviouslyApproved = props.editException?.previouslyApproved != undefined && !props.isForBulkEdit ?
      props.editException?.previouslyApproved : false;
    const originalExcludeFromCciioJustification = props.editException?.excludeFromCCIIOJustification != undefined && !props.isForBulkEdit ?
      props.editException?.excludeFromCCIIOJustification : false;

    formStateDispatch({
      payload: {
        comment: details.comment,
        endDate: originalEndDate,
        excludeFromCCIIOJustification: originalExcludeFromCciioJustification,
        grade: details.grade,
        narrative: details.exceptionNarrative,
        previouslyApproved: originalPreviouslyApproved,
        reviewStatus: details.reviewStatus,
        startDate: originalStartDate,
        status: props.isForArchived ? SpecialtyGroupExceptionStatus.None : props.editException.status,
        uploadedDocuments: details.supplementaryDocuments,
      },
      type: EditExceptionFormActionType.Reset
    });
  };

  useEffect(() => {
    if (!props.isForBulkEdit) {
      getExceptionDetails();
    }
  }, []);

  useEffect(() => {
    if (!props.isForBulkEdit || !bulkExceptions) return;

    if (bulkExceptions.length === 0) {
      toast("No exceptions found for the selected IDs.", { containerId: "z-index-9999", type: "warning" });
      return;
    }

    const first = bulkExceptions[0];
    const formatDate = (date: unknown) => date ? moment(date as string).format("YYYY-MM-DD") : '';
    const allSame = (getValue: (e: SpecialtyGroupException) => unknown) =>
      bulkExceptions.every(e => getValue(e) === getValue(first));

    const sameStatus = allSame(e => e.specialtyGroupExceptionStatus);
    const sameGrade = allSame(e => e.grade);
    const sameReviewStatus = allSame(e => e.reviewStatus);
    const samePreviouslyApproved = allSame(e => e.previouslyApproved);
    const sameComment = allSame(e => normalizeSpaces(e.comment ?? ''));
    const sameNarrative = allSame(e => normalizeSpaces(e.exceptionNarrative ?? ''));
    const sameStartDate = allSame(e => formatDate(e.startDate));
    const sameEndDate = allSame(e => formatDate(e.endDate));
    const sameExcludeFromCCIIOJustification = allSame(e => e.excludeFromCCIIOJustification);

    const newMixedFields = new Set<string>();
    if (!sameStatus) newMixedFields.add('status');
    if (!sameGrade) newMixedFields.add('grade');
    if (!sameReviewStatus) newMixedFields.add('reviewStatus');
    if (!samePreviouslyApproved) newMixedFields.add('previouslyApproved');
    if (!sameComment) newMixedFields.add('comment');
    if (!sameNarrative) newMixedFields.add('narrative');
    if (!sameStartDate) newMixedFields.add('startDate');
    if (!sameEndDate) newMixedFields.add('endDate');
    if (!sameExcludeFromCCIIOJustification) newMixedFields.add('excludeFromCCIIOJustification');
    setMixedFields(newMixedFields);

    formStateDispatch({
      payload: {
        comment: sameComment ? normalizeSpaces(first.comment ?? '') : '',
        endDate: sameEndDate ? formatDate(first.endDate) : '',
        excludeFromCCIIOJustification: sameExcludeFromCCIIOJustification ? first.excludeFromCCIIOJustification : undefined,
        grade: sameGrade ? first.grade : SpecialtyGroupExceptionGrade.None,
        narrative: sameNarrative ? normalizeSpaces(first.exceptionNarrative ?? '') : '',
        previouslyApproved: samePreviouslyApproved ? first.previouslyApproved : undefined,
        reviewStatus: sameReviewStatus ? first.reviewStatus : SpecialtyGroupExceptionReviewStatus.None,
        startDate: sameStartDate ? formatDate(first.startDate) : '',
        status: sameStatus ? first.specialtyGroupExceptionStatus : SpecialtyGroupExceptionStatus.None,
        uploadedDocuments: [],
      },
      type: EditExceptionFormActionType.Reset
    });
  }, [bulkExceptions]);

  const handleSave = async () => {
    try {
      if (props.isForBulkEdit) {
        setIsBulkEditConfirmationModalOpen(true);
      } else {
        const updateException: UpdateSpecialtyGroupException = {
          comment: formState.comment,
          endDate: formState.endDate ? moment(formState.endDate).toDate() : undefined,
          exceptionNarrative: formState.narrative,
          excludeFromCCIIOJustification: formState.excludeFromCCIIOJustification,
          grade: formState.grade,
          previouslyApproved: formState.previouslyApproved,
          reviewStatus: formState.reviewStatus,
          specialtyGroupExceptionId: props.editException.exceptionId,
          specialtyGroupExceptionStatus: formState.status,
          startDate: formState.startDate ? moment(formState.startDate).toDate() : undefined,
          supplementaryDocuments: formState.uploadedDocuments
        };

        await exceptionsService.updateSpecialtyGroupException(props.editException.customProjectId, updateException);

        flyoutContext.handleCloseFlyout();
        props.refreshData();

        toast("Exception Status successfully updated!",
          {
            containerId: "z-index-9999",
            type: "success"
          }
        );

        exceptionFilterContext.setRefreshUnprocessedExceptionsBanner(true);
        if (props.isForArchived) {
          exceptionFilterContext.reloadFilters();
        }
      }
    } catch (e) {
      toast("Exception Status failed to update!",
        {
          containerId: "z-index-9999",
          type: "error"
        }
      );
    }
  }

  const handleBulkSave = async () => {
    try {
      const bulkExceptionEditModel: BulkExceptionEditModel = {
        selectedExceptionIds: exceptionFilterContext.selectedExceptionIds,
        ...(formState.comment !== formState.originalComment && formState.comment !== '' && { comment: formState.comment }),
        ...(formState.endDate !== formState.originalEndDate && formState.endDate !== '' && { endDate: moment(formState.endDate).toDate() }),
        ...(formState.narrative !== formState.originalNarrative && formState.narrative !== '' && { exceptionNarrative: formState.narrative }),
        ...(formState.grade !== formState.originalGrade && formState.grade !== SpecialtyGroupExceptionGrade.None && { grade: formState.grade }),
        ...(formState.previouslyApproved !== formState.originalPreviouslyApproved && { previouslyApproved: formState.previouslyApproved }),
        ...(formState.reviewStatus !== formState.originalReviewStatus && formState.reviewStatus !== SpecialtyGroupExceptionReviewStatus.None && { reviewStatus: formState.reviewStatus }),
        ...(formState.status !== formState.originalStatus && formState.status !== SpecialtyGroupExceptionStatus.None && { specialtyGroupExceptionStatus: formState.status }),
        ...(formState.startDate !== formState.originalStartDate && formState.startDate !== '' && { startDate: moment(formState.startDate).toDate() }),
        ...(formState.excludeFromCCIIOJustification !== formState.originalExcludeFromCCIIOJustification && { excludeFromCCIIOJustification: formState.excludeFromCCIIOJustification }),
      };

      await exceptionsService.bulkUpdateExceptionsByIds(bulkExceptionEditModel);

      setIsBulkEditConfirmationModalOpen(true);

      toast(`The selected exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} ${exceptionFilterContext.selectedExceptionIds.length == 1 ? "has" : "have"} been successfully updated!`,
        {
          containerId: "z-index-9999",
          type: "success"
        }
      );
      exceptionFilterContext.setRefreshUnprocessedExceptionsBanner(true);
    } catch (e) {
      toast(`The selected exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} failed to update!`,
        {
          containerId: "z-index-9999",
          type: "error"
        }
      );
    } finally {
      exceptionFilterContext.setSelectedExceptionIds([]);
      exceptionFilterContext.setTriggerRefreshData(true);
      flyoutContext.handleCloseFlyout();
    }
  }

  const handleDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  };

  const handleFileUpload = async (file: File) => {

    return new Promise<void>(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append(uploadDocumentsApiKey, file);
        const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
        const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);
        if (!!metadataId && fileinfo instanceof File) {
          const supplementaryDocument: SupplementaryDocument = {
            documentName: fileinfo.name ? fileinfo.name : '',
            supplementaryDocumentMetadataId: metadataId
          }
          formStateDispatch({ payload: [supplementaryDocument], type: EditExceptionFormActionType.AddUploadedDocuments })
          multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(metadataId, file));
          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }

    });
  };

  const exceptionSharedInsightsMessage: string = useMemo(() => {
    if (!props.editException || !showSharedInsightsForClient) {
      return null;
    }

    return getExceptionSharedInsightsMessage(
      props.editException.exceptionSharedInsightsPreviousYearApprovalsCount,
      props.editException.exceptionSharedInsightsInProgressCount
    );
  }, [props.editException, showSharedInsightsForClient]);

  const gapSharedInsightsMessage: string = useMemo(() => {
    if (!props.editException || !showSharedInsightsForClient) {
      return null;
    }

    return getGapSharedInsightsMessage(
      props.editException.gapSharedInsightsCount,
      props.editException.gapSharedInsightsPayerCount
    );
  }, [props.editException, showSharedInsightsForClient]);

  return (
    <>
      <div className="row">

        <div className="col-10 mb-3">
          <QESAlert type="info" persist={true}>
            If any fields are changed/updated in this screen, it will update all selected exceptions. If no changes are made, the fields will remain the same on the selected exceptions.
          </QESAlert>

          {isLoadingBulkDetails && <div className="mt-3">Loading exception details...</div>}

          {!props.isForBulkEdit && props.editException &&
            <div className="mt-3 mb-4">
              <div><span className="demibold">Service Area: </span>{props.editException.serviceArea}</div>
              <div><span className="demibold">Specialty: </span>{`${props.editException.specialty} (${props.editException.specialtyGroupCode})`}</div>
              {showSharedInsightsForClient && props.editException.hasProjectExceptionPackage &&
                <>
                  {exceptionSharedInsightsMessage &&
                    <div><span className="demibold">Shared Insights Exceptions: </span>{exceptionSharedInsightsMessage}</div>}
                  {gapSharedInsightsMessage &&
                    <div><span className="demibold">Shared Insights Gaps: </span>{gapSharedInsightsMessage}</div>}
                </>}
            </div>
          }

          <QESSelect
            id={"edit-exception-flyout__status-dropdown"}
            containerClassName="mt-3 p-0"
            label="Status this year"
            onChange={(e) => formStateDispatch({
              payload: e.target.value as SpecialtyGroupExceptionStatus,
              type: EditExceptionFormActionType.Status
            })}
            testId="edit-exception-flyout__status-dropdown"
            value={formState.status}
          >
            {props.exceptionStatusOptions.map(option => (
              <option key={option.key} value={option.key}>
                {option.name === 'None'
                  ? (mixedFields.has('status') ? 'Different Statuses Being Used' : '')
                  : option.name}
              </option>
            ))};
          </QESSelect>

          <div className="mt-3">
            <QESDateInput
              containerClassName="p-0"
              errorText="Start Date must be before Expiration Date"
              isValid={formState.validStartDate}
              label="Start Date"
              testId="edit-exception-flyout__start-date"
              onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.StartDate })}
              unrequiredValidation={true}
              value={formState.startDate}
            />
            {mixedFields.has('startDate') && <small className="form-text text-muted">Different Start Dates Being Used</small>}
          </div>

          <div className="mt-3">
            <QESDateInput
              containerClassName="p-0"
              errorText="Expiration Date must be after Start Date"
              isValid={formState.validEndDate}
              label="Expiration Date"
              testId="edit-exception-flyout__end-date"
              onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.EndDate })}
              unrequiredValidation={true}
              value={formState.endDate}
            />
            {mixedFields.has('endDate') && <small className="form-text text-muted">Different Expiration Dates Being Used</small>}
          </div>

          <div className="p-0">
            <TooltipWrapper
              text={disableGradeEdits ? 'Users with sufficient permissions may edit the Exception Grade.' : null}
              containerClassName={disableGradeEdits ? gradeContainerClasses : ''}
              triggerWidth="100%">
              <div
                className={disableGradeEdits ? '' : gradeContainerClasses}>
                <QESSelect
                  className="form-control"
                  value={formState.grade}
                  onChange={e => formStateDispatch({
                    payload: e.target.value as SpecialtyGroupExceptionGrade,
                    type: EditExceptionFormActionType.Grade
                  })}
                  label="Exception Grade"
                  testId="edit-exception-flyout__grade-dropdown"
                  disabled={disableGradeEdits}
                  validationTooltipContent={`A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.
B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n
C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.`}
                >
                  {Object.keys(SpecialtyGroupExceptionGrade).map((grade) => {
                    const value: SpecialtyGroupExceptionGrade = (SpecialtyGroupExceptionGrade)[grade as keyof typeof SpecialtyGroupExceptionGrade];
                    if (value === SpecialtyGroupExceptionGrade.None) {
                      return (
                        <option key={value} value={value}>
                          {mixedFields.has('grade') ? 'Different Exception Grades Being Used' : '---'}
                        </option>
                      );
                    }
                    return (
                      <option key={value} value={value}>{grade}</option>
                    );
                  })}
                </QESSelect>
              </div>
            </TooltipWrapper>
          </div>

          <div className="mt-3 p-0">
            <QESSelect
              className="form-control"
              value={formState.reviewStatus}
              onChange={e => formStateDispatch({
                payload: e.target.value as SpecialtyGroupExceptionReviewStatus,
                type: EditExceptionFormActionType.ReviewStatus
              })}
              label="Internal Review Status"
              testId="edit-exception-flyout__review-status-dropdown"
              disabled={disableReviewStatusEdits}
            >
              {Object.values(SpecialtyGroupExceptionReviewStatus).map((reviewStatus) => (
                <option key={reviewStatus} value={reviewStatus}>
                  {reviewStatus === SpecialtyGroupExceptionReviewStatus.None && mixedFields.has('reviewStatus')
                    ? 'Different Statuses Being Used'
                    : SpecialtyGroupExceptionReviewStatusLabels[reviewStatus]}
                </option>
              ))}
            </QESSelect>
          </div>

          <TooltipWrapper text={formState.previouslyApproved === undefined ? 'Selected exceptions have different values for Previously Approved' : null}>
            <QESCheckbox
              containerClassName="mt-3"
              label="Previously Approved"
              checked={formState.previouslyApproved ?? false}
              indeterminate={formState.previouslyApproved === undefined}
              onChange={(e) => formStateDispatch({
                payload: (e.target as HTMLInputElement).checked,
                type: EditExceptionFormActionType.PreviouslyApproved
              })}
              id="edit-exception-flyout__previously-approved"
              testId="edit-exception-flyout__previously-approved"
            />
          </TooltipWrapper>
          {featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT) && (isCCIIO || props.isBulkCCIIO) && (
            <TooltipWrapper text={formState.excludeFromCCIIOJustification === undefined ? 'Selected exceptions have different values for Exclude from CCIIO Justification' : null}>
              <QESCheckbox
                  containerClassName="mt-3"
                  label="Select this box if exception(s) should not be included in the CCIIO Justification Form output"
                  checked={formState.excludeFromCCIIOJustification ?? false}
                  indeterminate={formState.excludeFromCCIIOJustification === undefined}
                  onChange={(e) => formStateDispatch({
                      payload: (e.target as HTMLInputElement).checked,
                      type: EditExceptionFormActionType.ExcludeFromCciioJustification
                  })}
                  id="edit-exception-flyout__exclude-from-cciio"
                  testId="edit-exception-flyout__exclude-from-cciio"
              />
            </TooltipWrapper>
          )}

          <QESTextArea
            id={"edit-exception-flyout__comment-input"}
            containerClassName="mt-3"
            rows={4}
            value={formState.comment}
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Comment })}
            onBlur={() => formStateDispatch({ payload: normalizeSpaces(formState.comment), type: EditExceptionFormActionType.Comment })}
            maxLength={4000}
            label="Comments"
            placeholder={mixedFields.has('comment') ? 'Different Comments Being Used — leave blank to keep existing' : undefined}
            testId="edit-exception-flyout__comment-input"
          />

          <QESTextArea
            id={"edit-exception-flyout__narrative-input"}
            containerClassName="mt-3"
            rows={8}
            value={formState.narrative}
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Narrative})}
            onBlur={() => formStateDispatch({ payload: normalizeSpaces(formState.narrative), type: EditExceptionFormActionType.Narrative})}
            maxLength={4000}
            label="Exception Narrative"
            placeholder={mixedFields.has('narrative') ? 'Different Narratives Being Used — leave blank to keep existing' : undefined}
            testId="edit-exception-flyout__narrative-input"
          />

          {!props.isForBulkEdit &&
            <DragAndDropMultiFileUploader
              apiKeyName={uploadDocumentsApiKey}
              title="Attach Supplemental Documents. Attachments must be in .csv, .doc, .docx, .png, .jpg, .pdf, .xls, or .xlsx format."
              filetype=".csv,.doc,.docx,.png,.jpg,.jpeg,.pdf,.xls,.xlsx"
              onMultipleUpload={handleFileUpload}
              onRemove={key => formStateDispatch({ payload: key, type: EditExceptionFormActionType.RemoveUploadedDocumentByKey })}
              handleDownloadLink={handleDownload}
              small={true}
              testId="volume-exception-edit-upload-supplementary-docs"
            />
          }

        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="gap-fill-status-status-flyout__save"
            disabled={!isSaveEnabled()}
            onClick={() => {
              handleSave().then();
            }}
          >
            Save
          </QESButton>}
      />

      <BulkEditConfirmationModal
        isModalOpen={isBulkEditConfirmationModalOpen}
        setIsModalOpen={setIsBulkEditConfirmationModalOpen}
        handleConfirm={handleBulkSave}
      />

    </>
  );
}

const EditExceptionFlyout: FC<React.PropsWithChildren<EditExceptionFlyoutProps>> = (props: EditExceptionFlyoutProps) => (
  <DragAndDropMultiFileUploaderContextProvider>
    <WrappedEditExceptionFlyout {...props} />
  </DragAndDropMultiFileUploaderContextProvider>
);

export default EditExceptionFlyout;
